import type { Editor, JSONContent } from "@tiptap/core"
import { GapCursor } from "@tiptap/pm/gapcursor"
import type { ResolvedPos } from "@tiptap/pm/model"
import type { Transaction } from "@tiptap/pm/state"

/**
 * Inserts content as a new block after the block containing `pos`, instead of splitting
 * that block. If `pos` is undefined, inserts at the current selection instead.
 *
 * Ends by setting the selection to a gap cursor just after the inserted block.
 */
export function insertBlockContent(editor: Editor, content: JSONContent[], pos?: number): void {
  const sizeBefore = editor.state.doc.content.size

  if (pos !== undefined) {
    // We advance to just before the next block, or the end of the current card.
    const insertPos = blockAfterPos(editor.state.doc.resolve(pos)) ?? pos
    editor
      .chain()
      .insertContentAt(insertPos, content)
      .command(({ tr }) => gapCursorAfterInsert(tr, insertPos, sizeBefore))
      .focus()
      .run()
  } else {
    // insertContent replaces the current selection, so account for the deleted range.
    const { from, to } = editor.state.selection
    editor
      .chain()
      .insertContent(content)
      .command(({ tr }) => gapCursorAfterInsert(tr, from, sizeBefore, to - from))
      .focus()
      .run()
  }
}

/**
 * Sets the transaction's selection to a gap cursor just after the block that was inserted
 * at `insertStart`. `deleted` is the size of any range the insert replaced.
 */
function gapCursorAfterInsert(
  tr: Transaction,
  insertStart: number,
  sizeBefore: number,
  deleted = 0,
): boolean {
  const insertedSize = tr.doc.content.size - sizeBefore + deleted
  tr.setSelection(new GapCursor(tr.doc.resolve(insertStart + insertedSize)))
  return true
}

/**
 * Given a position, returns the position just after the block containing it,
 * i.e., just before the next block or the end of the current card.
 *
 * If pos is inside a cardTitle, returns the position
 * just after the title (the first block slot). Returns null if pos is not inside
 * a card child (e.g., directly in a card after all its blocks),
 * in which case pos is already a valid block boundary.
 */
function blockAfterPos($pos: ResolvedPos): number | null {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const parent = $pos.node(depth - 1)
    if (["titledCard", "untitledCard"].includes(parent.type.name)) {
      // $pos is inside a direct child of the card: a block
      // or the cardTitle. Insert just after it.
      return $pos.after(depth)
    }
  }
  return null
}
